// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Import
import CustomTextField from "components/mui/text-field";

// ** API Imports
import { SaveInvoice } from "screens/Invoice/logic/SaveInvoice";
import { GetAssessmentAmount } from "screens/Invoice/logic/GetAssessmentAmount";
import { SaveAssessmentAmount } from "screens/Invoice/logic/SaveInvoice";

// ** Third-Party Imports
import dayjs from "dayjs";
import { toast } from "react-toastify";

const GenerateInvoice = ({ open, handleClose, assessmentID, setLoading, openScheduleAst }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [assessmentAmount, setAssessmentAmount] = useState();
  const [workRequirement, setWorkRequirement] = useState();
  const [jobID, setJobID] = useState(0);
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceID: 0,
    requestID: Number(assessmentID),
    invoiceTitle: "",
    invoiceType: "Assessment",
    invoiceJobs: [
      {
        invoiceJobID: 0,
        jobID: jobID,
        billedAmount: 0,
        taxRate: 0,
        taxAmount: 0,
      },
    ],
    invoiceDate: dayjs(),
    dueDate: dayjs().add(30, "days"),
    status: "Draft",
    paymentMethods: [],
    notes: [],
    additionalPaymentTerms: [],
  });
  const [errors, setErrors] = useState({
    assessmentAmount: false,
  });

  useEffect(() => {
    const fetchWorkRequirements = async () => {
      try {
        
        const response = await GetAssessmentAmount(assessmentID);

        if(!response.isError){
          const workReq = response.result.find((wr) => wr.subServiceID === 0);
          const job = workReq?.jobs.find((wr) => wr.occurrenceNo === 0)

          setWorkRequirement(workReq);
          setJobID(job.jobID);
          setAssessmentAmount(job.flatRate);
          setInvoiceDetails((prev) => ({
            ...prev,
            invoiceJobs: [
              {
                invoiceJobID: 0,
                jobID: job.jobID,
                billedAmount: job.flatRate,
                taxRate: 0,
                taxAmount: 0,
              },
            ],
          }));
        } else {
          console.warn("No work requirements found with subServiceID = 0");
          setWorkRequirement(null);
          setJobID(null);
        }
      } catch (error) {
        console.error(error);
      }
    };
  
    fetchWorkRequirements();
  }, [assessmentID, openScheduleAst]);

  const handleChangeInvoiceDetails = (name, value) => {
    let processedValue = value;

    if (name === "assessmentAmount") {
      const numericString = value.replace(/[^0-9.]/g, '');
      
      const parts = numericString.split('.');
      let wholePart = parts[0];
      const decimalPart = parts.length > 1 ? `.${parts[1]}` : '';
  
      wholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
      processedValue = wholePart + decimalPart;
    }

    setAssessmentAmount(processedValue);

    const numericValue = parseFloat(processedValue.replace(/,/g, '')) || 0;

    setInvoiceDetails((prev) => ({
      ...prev,
      invoiceJobs: [
        {
          invoiceJobID: 0,
          jobID: jobID,
          billedAmount: numericValue,
          taxRate: 0,
          taxAmount: 0,
        },
      ],
    }));
  };

  const handleCreateInvoice = async () => {
    if (!assessmentAmount || assessmentAmount <= 0) {
      setErrors({
        assessmentAmount: true,
      });
      return;
    }
    setLoading(true);
    try {
      const responseInvoice = await SaveInvoice(invoiceDetails, navigate);

      if (!responseInvoice.isError) {
        const updatedWorkRequirement = {
          ...workRequirement,
          jobs: workRequirement.jobs.map((job) => ({
            ...job,
            flatRate: Array.isArray(invoiceDetails.invoiceJobs) ? invoiceDetails?.invoiceJobs[0]?.billedAmount : 0,
            assignees: job.assignees
              ? job.assignees.map((assignee) => assignee.employeeID)
              : null,
              status:'unscheduled'
          })),
        };

        setWorkRequirement(updatedWorkRequirement);

        try {
          const responseAstAmount = await SaveAssessmentAmount(
            updatedWorkRequirement,
            navigate
          );

          if (responseAstAmount.isError) {
            toast.error("Invoice Created Successfully but failed to save the amount", {
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });
          } else {
            toast.success("Invoice Created Successfully", {
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          });
            navigate(`/invoices/detail/${responseInvoice.result.invoiceID}`);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        toast.error(responseInvoice.message || "Failed to create invoice", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrencyDisplay = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return isNaN(num) ? '' : num.toLocaleString();
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={() => handleClose}
        PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "40vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Typography variant="h4" component="h2">
            Generate Invoice
          </Typography>
          <Tooltip title="Close">
            <IconButton onClick={handleClose}>
              <Icon icon="tabler:x" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Typography variant="h6">Invoice Summary</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>Assessment ID:</Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography>{`AST-${assessmentID?.padStart(3, "0")}`}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                Amount:{" "}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <CustomTextField
                placeholder="0.00"
                value={formatCurrencyDisplay(assessmentAmount)}
                onChange={(e) =>
                  handleChangeInvoiceDetails("assessmentAmount", e.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                error={errors.assessmentAmount}
                helperText={
                  errors.assessmentAmount ? "Enter a value greater than 0" : ""
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
        >
          <Button variant="contained" color="info" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={handleCreateInvoice}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateInvoice;
